import React, { useState } from 'react';
import {
  Apple,
  ShoppingBag,
  Heart,
  BookOpen,
  Users,
  LayoutDashboard,
  Bell,
  ChevronRight,
  CheckCircle2,
  Star,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/YOUR_LINK_HERE';

// ─── Feature Cards ────────────────────────────────────────────────────────────
const features = [
  {
    icon: <ShoppingBag size={22} />,
    title: 'Shop',
    description:
      'Discover nutrition products tailored to your health goals, with ingredient-level transparency powered by Open Food Facts.',
  },
  {
    icon: <Heart size={22} />,
    title: 'Care Hub',
    description:
      'Manage care profiles for yourself and loved ones. Track routines, dietary needs, and wellness milestones in one place.',
  },
  {
    icon: <BookOpen size={22} />,
    title: 'Recipes',
    description:
      'Explore curated recipes with smart ingredient parsing, one-tap shopping list generation, and cookbook collections.',
  },
  {
    icon: <Users size={22} />,
    title: 'Community',
    description:
      'Connect with others on their wellness journey. Share recipes, tips, and celebrate milestones together.',
  },
  {
    icon: <LayoutDashboard size={22} />,
    title: 'Dashboard',
    description:
      'Your personal health snapshot. Activity, progress, and everything Nutrius in one beautifully designed home.',
  },
  {
    icon: <Bell size={22} />,
    title: 'Notifications',
    description:
      'Stay updated with app announcements, community activity, and personalized health reminders.',
  },
];

// ─── What we're testing ───────────────────────────────────────────────────────
const testingAreas = [
  'Overall navigation and tab switching',
  'Recipe browsing and shopping list creation',
  'Care profile management',
  'Product discovery and linking',
  'Community feed and interactions',
  'Performance across different iPhone models',
];

// ─── Component ────────────────────────────────────────────────────────────────
const BetaPage = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(TESTFLIGHT_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Soft radial glow behind hero content */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% -10%, var(--color-primary, #22c55e) 0%, transparent 70%)',
            opacity: 0.08,
          }}
        />

        <div className="max-w-7xl mx-auto px-lg pt-xxxl pb-xxl text-center relative z-10">
          {/* Badge */}
          <div className="inline-flex items-center gap-sm bg-primary/10 text-primary px-md py-xs rounded-full mb-xl">
            <Sparkles size={14} />
            <span className="text-footnote font-semibold tracking-wide uppercase">
              Beta Programme
            </span>
          </div>

          <h1 className="text-large-title font-rounded text-primary mb-lg leading-tight">
            You're invited to<br />test Nutrius
          </h1>

          <p className="text-body text-text-secondary max-w-xl mx-auto mb-xxl leading-relaxed">
            Nutrius is a complete health and nutrition platform — shop smarter,
            cook better, and build lasting wellness habits. We're opening early
            access via Apple TestFlight and we'd love your help shaping what
            comes next.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-md mb-xl">
            <a
              href={TESTFLIGHT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-sm bg-primary text-white px-xxl py-md rounded-full text-callout font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-95"
            >
              <Apple size={20} />
              Join on TestFlight
              <ArrowRight size={16} />
            </a>

            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-sm border border-text-tertiary/30 text-text-secondary px-xl py-md rounded-full text-callout font-semibold hover:border-primary hover:text-primary transition-all"
            >
              {copied ? (
                <>
                  <CheckCircle2 size={16} className="text-primary" />
                  Link copied!
                </>
              ) : (
                'Copy invite link'
              )}
            </button>
          </div>

          <p className="text-caption text-text-tertiary">
            Requires iOS 16 or later · Free to join · No App Store purchase needed
          </p>
        </div>
      </section>

      {/* ── Divider ───────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-lg">
        <div className="border-t border-text-tertiary/10" />
      </div>

      {/* ── Features ──────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-lg py-xxl">
        <div className="text-center mb-xxl">
          <h2 className="text-title-1 font-rounded text-primary mb-md">
            Everything in the beta
          </h2>
          <p className="text-body text-text-secondary max-w-lg mx-auto">
            This build includes all five core tabs — here's what you'll find inside.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-lg">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-surface border border-text-tertiary/10 rounded-large p-xl hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <div className="w-11 h-11 rounded-large bg-primary/10 flex items-center justify-center text-primary mb-md group-hover:bg-primary group-hover:text-white transition-all">
                {f.icon}
              </div>
              <h3 className="text-headline font-rounded text-primary mb-sm">
                {f.title}
              </h3>
              <p className="text-footnote text-text-secondary leading-relaxed">
                {f.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── What we're testing ────────────────────────────────────────────── */}
      <section className="bg-surface border-y border-text-tertiary/10">
        <div className="max-w-7xl mx-auto px-lg py-xxl">
          <div className="grid md:grid-cols-2 gap-xxl items-center">
            <div>
              <h2 className="text-title-1 font-rounded text-primary mb-md">
                What we're testing
              </h2>
              <p className="text-body text-text-secondary mb-xl leading-relaxed">
                Your feedback directly shapes what we build before public launch.
                We're especially interested in how Nutrius feels to use day-to-day.
              </p>
              <ul className="space-y-md">
                {testingAreas.map((area) => (
                  <li key={area} className="flex items-start gap-sm">
                    <CheckCircle2
                      size={18}
                      className="text-primary mt-0.5 flex-shrink-0"
                    />
                    <span className="text-callout text-text-secondary">{area}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-background border border-text-tertiary/10 rounded-large p-xl">
              <div className="flex items-center gap-sm mb-lg">
                <Star size={18} className="text-primary fill-primary" />
                <span className="text-callout font-semibold text-primary">
                  How to share feedback
                </span>
              </div>
              <ol className="space-y-lg">
                {[
                  {
                    step: '1',
                    title: 'Shake to report',
                    body: 'Shake your iPhone at any time inside the app to instantly submit a bug report with a screenshot.',
                  },
                  {
                    step: '2',
                    title: 'Use TestFlight feedback',
                    body: 'Tap the feedback button in the TestFlight app after each build to rate and comment.',
                  },
                  {
                    step: '3',
                    title: 'Email us directly',
                    body: (
                      <>
                        Reach us anytime at{' '}
                        <a
                          href="mailto:hello@nutrius.co"
                          className="text-primary underline underline-offset-2"
                        >
                          hello@nutrius.co
                        </a>
                        . We read every message.
                      </>
                    ),
                  },
                ].map((item) => (
                  <li key={item.step} className="flex gap-md">
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-caption font-bold flex items-center justify-center flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="text-callout font-semibold text-primary mb-xs">
                        {item.title}
                      </p>
                      <p className="text-footnote text-text-secondary leading-relaxed">
                        {item.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── Known limitations ─────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-lg py-xxl">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-title-2 font-rounded text-primary mb-md">
            Known limitations
          </h2>
          <p className="text-body text-text-secondary mb-xl">
            A few things are still in progress — good to know before you dive in.
          </p>
          <div className="space-y-sm text-left">
            {[
              'The Notification Hub is a placeholder — the bell icon is visible but the full sheet is coming soon.',
              'Some product catalog content is still being added.',
              'Data in the beta environment may be reset between major builds.',
            ].map((note) => (
              <div
                key={note}
                className="flex items-start gap-sm bg-surface border border-text-tertiary/10 rounded-large px-lg py-md"
              >
                <span className="text-text-tertiary text-headline mt-0.5 flex-shrink-0">
                  ·
                </span>
                <p className="text-callout text-text-secondary">{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 80% at 50% 110%, var(--color-primary, #22c55e) 0%, transparent 70%)',
            opacity: 0.07,
          }}
        />
        <div className="max-w-7xl mx-auto px-lg py-xxxl text-center relative z-10">
          <h2 className="text-title-1 font-rounded text-primary mb-md">
            Ready to get started?
          </h2>
          <p className="text-body text-text-secondary mb-xxl max-w-md mx-auto">
            Join the Nutrius beta on TestFlight and help us build the future of
            personal nutrition.
          </p>
          <a
            href={TESTFLIGHT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-sm bg-primary text-white px-xxl py-md rounded-full text-callout font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-95"
          >
            <Apple size={20} />
            Join on TestFlight
            <ChevronRight size={16} />
          </a>
          <p className="text-caption text-text-tertiary mt-lg">
            © {new Date().getFullYear()} HealthWealth LLC · Made with ♥ for your health
          </p>
        </div>
      </section>

    </div>
  );
};

export default BetaPage;